import React, { useMemo } from 'react';
import FiltersCard from '../../../components/FiltersCard';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import { FilterField, FilterGrid } from '../../../components/ui';
import { activeFilterCount, type MaterialOption } from '../filters';
import type { AttentionFilter, Owner, SiteFilters } from '../types';
import { OWNERS } from '../types';

export interface AreaOption {
  id: string;
  name: string;
}

export interface ForemanAreaOption extends AreaOption {
  serviceAreaId: string;
}

interface LocationsFiltersProps {
  /** What the dropdowns show; becomes `applied` on Apply. */
  pending: SiteFilters;
  applied: SiteFilters;
  onChange: (next: SiteFilters) => void;
  onApply: () => void;
  onClear: () => void;
  serviceAreas: AreaOption[];
  foremanAreas: ForemanAreaOption[];
  materials: MaterialOption[];
}

const TYPE_OPTIONS = [
  { value: 'pit', label: 'Pits' },
  { value: 'yard', label: 'Yards' },
] as const;

export const ATTENTION_OPTIONS: { value: AttentionFilter; label: string }[] = [
  { value: 'any', label: 'All sites' },
  { value: 'needs-action', label: 'Needs action (any)' },
  { value: 'negative', label: 'Bins below zero' },
  { value: 'below-min', label: 'Bins below minimum' },
  { value: 'over-max', label: 'Bins over maximum' },
];

const same = (a: SiteFilters, b: SiteFilters) => JSON.stringify({ ...a, search: '' }) === JSON.stringify({ ...b, search: '' });

/**
 * The Locations filters, laid out the way QP Tracking lays out its own: one FiltersCard, a
 * FilterGrid of dropdowns, Apply to run them and Clear All to start over. Picking service areas
 * narrows the foreman areas offered, and picking foreman areas narrows the service areas.
 */
const LocationsFilters: React.FC<LocationsFiltersProps> = ({
  pending,
  applied,
  onChange,
  onApply,
  onClear,
  serviceAreas,
  foremanAreas,
  materials,
}) => {
  const set = <K extends keyof SiteFilters>(key: K, value: SiteFilters[K]) => onChange({ ...pending, [key]: value });

  const foremanOptions = useMemo(
    () =>
      (pending.serviceAreaIds.length ? foremanAreas.filter((area) => pending.serviceAreaIds.includes(area.serviceAreaId)) : foremanAreas)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [foremanAreas, pending.serviceAreaIds]
  );
  const serviceOptions = useMemo(() => {
    if (!pending.foremanAreaIds.length) return serviceAreas;
    const parents = new Set(foremanAreas.filter((area) => pending.foremanAreaIds.includes(area.id)).map((area) => area.serviceAreaId));
    return serviceAreas.filter((area) => parents.has(area.id));
  }, [serviceAreas, foremanAreas, pending.foremanAreaIds]);

  const count = activeFilterCount(applied);

  return (
    <FiltersCard
      title="Filters"
      collapsible
      showClearButton
      onClear={onClear}
      hasActiveFilters={count > 0}
      activeFilterCount={count}
      showApplyButton
      onApply={onApply}
      applyDisabled={same(pending, applied)}
    >
      <FilterGrid>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            label="Service Area"
            options={serviceOptions}
            value={pending.serviceAreaIds}
            onChange={(values) => set('serviceAreaIds', values.map(String))}
            getValue={(option: AreaOption) => option.id}
            formatOption={(option: AreaOption) => option.name}
            placeholder="All service areas"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            label="Foreman Area"
            options={foremanOptions}
            value={pending.foremanAreaIds}
            onChange={(values) => set('foremanAreaIds', values.map(String))}
            getValue={(option: ForemanAreaOption) => option.id}
            formatOption={(option: ForemanAreaOption) => option.name}
            placeholder="All foreman areas"
            noOptionsMessage="No foreman areas in the chosen service areas"
          />
        </FilterField>
        <FilterField span={2}>
          <MultiSelectDropdown
            label="Material"
            options={materials}
            value={pending.materialIds}
            onChange={(values) => set('materialIds', values.map(String))}
            getValue={(option: MaterialOption) => option.id}
            formatOption={(option: MaterialOption) => option.label}
            placeholder="Any material"
            showSearch
            noOptionsMessage="No material held in any bin"
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            label="Type"
            options={[...TYPE_OPTIONS]}
            value={pending.types}
            onChange={(values) => set('types', values as SiteFilters['types'])}
            getValue={(option) => option.value}
            formatOption={(option) => option.label}
            placeholder="Pits and yards"
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            label="Owner"
            options={OWNERS}
            value={pending.owners}
            onChange={(values) => set('owners', values as Owner[])}
            getValue={(owner: Owner) => owner}
            formatOption={(owner: Owner) => owner}
            placeholder="Any owner"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="searchable"
            label="Needs action"
            options={ATTENTION_OPTIONS}
            value={pending.attention}
            onChange={(value) => set('attention', (value || 'any') as AttentionFilter)}
            getValue={(option) => option.value}
            formatOption={(option) => option.label}
            preserveOptionsOrder
          />
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

export default LocationsFilters;
